import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils/cn";

export interface MenuProps {
  /** The element the menu is positioned under. Mirrors MUI's anchorEl API. */
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Minimum width in px (defaults to the anchor width, min 200). */
  minWidth?: number;
}

/**
 * Dropdown menu anchored to a trigger element — design-system replacement for
 * MUI `<Menu anchorEl … >`. Renders in a portal, positions under the anchor
 * (RTL-aware), closes on outside-click / Escape.
 */
const Menu = ({
  anchorEl,
  open,
  onClose,
  children,
  className,
  minWidth,
}: MenuProps) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Two-pass positioning: render hidden, measure, then clamp fully into the
  // viewport (shift back from either edge, flip above the anchor if no room
  // below). Mirrors MUI's collision avoidance so the menu is never cut off.
  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setPos(null);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const rtl = document.documentElement.getAttribute("dir") !== "ltr";
    const M = 8; // viewport margin
    const el = menuRef.current;
    const width = Math.max(el?.offsetWidth ?? 0, minWidth ?? rect.width, 200);
    const height = el?.offsetHeight ?? 0;

    // preferred horizontal: align the menu's start edge with the anchor's start edge
    let left = rtl ? rect.right - width : rect.left;
    left = Math.min(Math.max(left, M), window.innerWidth - width - M);

    // preferred vertical: below the anchor; flip above if it would overflow
    let top = rect.bottom + 4;
    if (height && top + height > window.innerHeight - M) {
      const above = rect.top - 4 - height;
      top = above >= M ? above : Math.max(M, window.innerHeight - height - M);
    }
    setPos({ top, left, width });
  }, [open, anchorEl, minWidth, children]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-popover" onClick={onClose}>
      <div
        ref={menuRef}
        role="menu"
        onClick={(e) => e.stopPropagation()}
        style={
          pos
            ? {
                position: "fixed",
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
              }
            : {
                position: "fixed",
                top: 0,
                left: 0,
                visibility: "hidden",
                minWidth: minWidth ?? 200,
              }
        }
        className={cn(
          "max-h-[80vh] animate-scale-in overflow-y-auto rounded-2xl border border-border-subtle bg-surface p-1.5 text-menu shadow-e3",
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

const MenuItem = ({ children, className, ...rest }: MenuItemProps) => (
  <button
    type="button"
    role="menuitem"
    className={cn(
      "transition-colors flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm text-menu hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
    {...rest}
  >
    {children}
  </button>
);

const MenuDivider = ({ className }: { className?: string }) => (
  <div
    role="separator"
    className={cn("my-1 h-px bg-border-subtle", className)}
  />
);

Menu.Item = MenuItem;
Menu.Divider = MenuDivider;

export default Menu;
